import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { and, not } from 'ember-truth-helpers';
import ApprovalBlocklistReport from 'garaje/components/approval-blocklist-report';
import MultipleScreenersInfo from 'garaje/components/approvals/multiple-screeners-info';
import ReviewButtons from 'garaje/components/approvals/screeners/review-buttons';
import UnifiedScreenersModal from 'garaje/components/approvals/unified-screeners-modal';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import UiButton from 'garaje/components/ui-button';
import type ApprovalStatusModel from 'garaje/models/approval-status';
import type { ReportSource } from 'garaje/models/approval-status';
import type ScreenerResult from 'garaje/models/screener-result';
import type { BlocklistReport, IdScanningReport, ReportWithReason } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type VisitApproval from 'garaje/models/visit-approval';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import getModelClassName from 'garaje/utils/get-model-class-name';
import { faceHoldCopy } from 'garaje/utils/id-scanning-face-copy';
import { idScanningHoldFeedMessage } from 'garaje/utils/id-scanning-hold-copy';

interface FeedItemFlaggedScreenerSignature<T extends GuestWithVisitApproval> {
  Args: {
    approvalStatus?: ApprovalStatusModel;
    canReview: boolean;
    canReviewOrRead: boolean;
    model: T;
    visitApproval: VisitApproval;
    approveTask: Task<void, [screener?: ReportSource[]]>;
    denyTask?: Task<void, []>;
    multipleApprovals: boolean;
    /** Approval context forwarded to the screeners modal. */
    context?: 'location' | 'property';
  };
}

export default class FeedItemFlaggedScreener<T extends GuestWithVisitApproval> extends Component<
  FeedItemFlaggedScreenerSignature<T>
> {
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked focusedResult?: ScreenerResult | null = null;

  get blocklistApproval(): ScreenerResult<BlocklistReport> | ApprovalStatusModel | undefined {
    return this.#getApproval('blocklistReviewResult', 'didFailBlocklistCheck');
  }

  get preRegistrationRequiredApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('preRegistrationRequiredReviewResult', 'didFailPreRegistrationRequiredCheck');
  }

  get identityVerificationApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('identityVerificationReviewResult', 'didFailIdVerificationCheck');
  }

  get visitorDocumentsApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('visitorDocumentsReviewResult', 'visitorDocumentsCheck');
  }

  get visualComplianceApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('visualComplianceReviewResult', 'didFailVisualComplianceCheck');
  }

  get tempScreeningApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('tempScreeningReviewResult', 'didFailTempScreenCheck');
  }

  get capacityScreeningApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('capacityScreeningReviewResult', 'isAtCapacity');
  }

  get outgoingInviteApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('outgoingInviteReviewResult', 'inviteApproval');
  }

  get idScanningApproval(): ScreenerResult<IdScanningReport> | ApprovalStatusModel | undefined {
    return this.#getApproval('idScanningReviewResult', 'didFailIdScanningCheck');
  }

  get idScanningBypassed(): boolean {
    const report = this.idScanningApproval?.report;
    return Boolean(report && !Array.isArray(report) && report.bypass);
  }

  get idScanningBypassMessage(): string {
    const report = this.idScanningApproval?.report;
    if (report && !Array.isArray(report) && report.reason) return report.reason;
    return 'Visitor has requested ID assistance.';
  }

  /**
   * True when the ID scan flagged a name mismatch (vs. a rejected ID).
   */
  get idScanningNameMismatch(): boolean {
    if (!this.featureFlags.isEnabled('id-name-mismatch-screener')) return false;
    const report = this.idScanningApproval?.report;
    return Boolean(report && !Array.isArray(report) && report['name-match']?.mismatch);
  }

  get idScanningFaceHeld(): boolean {
    if (!this.featureFlags.isEnabled('id-face-match-screener')) return false;
    const report = this.idScanningApproval?.report;
    return Boolean(report && !Array.isArray(report) && report['face-match']?.held);
  }

  get idScanningFaceMessage(): string {
    const report = this.idScanningApproval?.report;
    const reason = report && !Array.isArray(report) ? report['face-match']?.reason : undefined;
    return faceHoldCopy(reason).feed;
  }

  /** Whatever held the visit once bypass and the name/photo mismatches are ruled out. */
  get idScanningHoldMessage(): string {
    const report = this.idScanningApproval?.report;
    return idScanningHoldFeedMessage(report && !Array.isArray(report) ? report : undefined);
  }

  get idScanningPendingApproval(): ScreenerResult | ApprovalStatusModel | undefined {
    return this.#getApproval('idScanningPendingResult', 'hasPendingIdScan');
  }

  get conditionalRuleApproval(): ScreenerResult<ReportWithReason> | undefined {
    return this.#getApproval('conditionalRuleReviewResult');
  }

  get resultWithDetails(): ScreenerResult | undefined {
    return this.args.visitApproval?.reviewResults.find((result) => result.details);
  }

  @action
  toggleModal(result?: ScreenerResult): void {
    this.focusedResult = result;

    if (!result) return;

    this.metrics.trackEvent('Unified Screener Modal Opened', {
      screenerSource: result?.screenerSource,
      from: `Log: ${getModelClassName(this.args.model)}`,
    });
  }

  @action
  didApprove(result: ScreenerResult, didApprove: () => void): void {
    this.metrics.trackEvent('Unified Screener Modal Approve Clicked', {
      screenerSource: result?.screenerSource,
      from: `Log: ${getModelClassName(this.args.model)}`,
    });

    didApprove();
  }

  @action
  didDeny(result: ScreenerResult): void {
    this.metrics.trackEvent('Unified Screener Modal Deny Clicked', {
      screenerSource: result?.screenerSource,
      from: `Log: ${getModelClassName(this.args.model)}`,
    });

    this.toggleModal(undefined);
  }

  #getApproval<V extends keyof VisitApproval>(visitApprovalKey: V): VisitApproval[V] | undefined;
  #getApproval<V extends keyof VisitApproval, A extends keyof ApprovalStatusModel>(
    visitApprovalKey: V,
    approvalStatusKey: A,
  ): VisitApproval[V] | ApprovalStatusModel | undefined;
  #getApproval<V extends keyof VisitApproval, A extends keyof ApprovalStatusModel>(
    visitApprovalKey: V,
    approvalStatusKey?: A,
  ): VisitApproval[V] | ApprovalStatusModel | undefined {
    if (this.args.visitApproval) {
      return this.args.visitApproval[visitApprovalKey];
    } else if (approvalStatusKey) {
      if (!this.args.approvalStatus?.[approvalStatusKey]) return undefined;
      return this.args.approvalStatus;
    }
    return;
  }

  <template>
    <div class="mt-2">
      {{#if @multipleApprovals}}
        <strong>Multiple approvals required</strong>
        <MultipleScreenersInfo
          @model={{@model}}
          @results={{@visitApproval.reviewResults}}
          @toggleModal={{this.toggleModal}}
        />
      {{else}}
        {{#if this.blocklistApproval}}
          <ApprovalBlocklistReport @approval={{this.blocklistApproval}} @model={{@model}} />
        {{/if}}
        {{#if (and this.preRegistrationRequiredApproval @canReview)}}
          <span data-test-pre-reg-required-details>Visitor was not invited.</span>
        {{/if}}
        {{#if (and this.identityVerificationApproval @canReview)}}
          <div data-test-id-verification-failed>
            <strong>Identity verification</strong>
            <div>{{this.identityVerificationApproval.idVerificationFailureMessage}}</div>
          </div>
        {{/if}}
        {{#if (and this.visitorDocumentsApproval @canReview)}}
          Visitor has uploaded documents that require your review.
        {{/if}}
        {{#if (and this.visualComplianceApproval @canReviewOrRead)}}
          <strong>Visual Compliance match.</strong>
        {{/if}}
        {{#if (and this.tempScreeningApproval @canReview)}}
          <div data-test-temp-screen-failed>
            <strong>Health screening</strong>
            <div>{{this.tempScreeningApproval.tempCheckFailureMessage}}</div>
          </div>
        {{/if}}
        {{#if (and this.capacityScreeningApproval @canReview)}}
          <span data-test-at-capacity>Location is at capacity.</span>
        {{/if}}
        {{#if (and this.idScanningApproval @canReview)}}
          {{#if this.idScanningBypassed}}
            <span data-test-id-scanning-invalid data-test-id-scanning-bypassed>{{this.idScanningBypassMessage}}</span>
          {{else if (and this.idScanningNameMismatch this.idScanningFaceHeld)}}
            <span data-test-id-scanning-invalid data-test-id-scanning-name-face-mismatch>
              Visitor’s name and photo do not match their government ID. Please review for entry.
            </span>
          {{else if this.idScanningNameMismatch}}
            <span data-test-id-scanning-invalid data-test-id-scanning-name-mismatch>
              Visitor name does not match their government ID. Please review for entry.
            </span>
          {{else if this.idScanningFaceHeld}}
            <span data-test-id-scanning-invalid data-test-id-scanning-face-mismatch>
              {{this.idScanningFaceMessage}}
            </span>
          {{else}}
            <span data-test-id-scanning-invalid>{{this.idScanningHoldMessage}}</span>
          {{/if}}
        {{/if}}
        {{#if (and this.idScanningPendingApproval @canReview)}}
          <div data-test-id-scanning-pending>
            <img src="/assets/images/progress-small-gray.svg" alt="" role="presentation" />&nbsp;
            <span class="whitespace-nowrap">
              Visitor’s ID is processing.
            </span>
          </div>
        {{/if}}
        {{#if (and this.outgoingInviteApproval @canReview)}}
          <strong data-test-outgoing-invite>Outgoing invite requires approval</strong>
        {{/if}}

        {{#if this.conditionalRuleApproval}}
          <div data-test-conditional-rule>
            <strong>Rule check</strong>
            {{#if this.conditionalRuleApproval.report.reason}}
              <div class="mt-1" data-test-conditional-rule-reason>
                <TruncateWithTooltip
                  @value={{this.conditionalRuleApproval.report.reason}}
                  @length={{50}}
                  @side="bottom"
                />
              </div>
            {{/if}}
          </div>
        {{/if}}
      {{/if}}
    </div>
    {{#if (and this.resultWithDetails (not @multipleApprovals))}}
      <div class="mt-2">
        <span>Review Match Details</span>
        <UiButton
          class="btn btn--link btn--small !text-gem-50"
          {{on "click" (fn this.toggleModal this.resultWithDetails)}}
          data-test-screener-results-detail-trigger
        >
          here
        </UiButton>
      </div>
    {{/if}}
    {{#if this.focusedResult}}
      <UnifiedScreenersModal
        @model={{@model}}
        @focusedResult={{this.focusedResult}}
        @onClose={{fn this.toggleModal null}}
        @context={{@context}}
      >
        <:actions as |result onApprove|>
          {{#if (and @canReview result.isReview)}}
            <ReviewButtons
              @approveTask={{@approveTask}}
              @denyTask={{@denyTask}}
              @result={{result}}
              @model={{@model}}
              @onApprove={{fn this.didApprove result onApprove}}
              @onDeny={{fn this.didDeny result}}
            >
              <:deny>
                Deny visit
              </:deny>
            </ReviewButtons>
          {{/if}}
        </:actions>
      </UnifiedScreenersModal>
    {{/if}}
  </template>
}
